import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { coachingEndpoints, type CoachingEndpointKey } from "../services/coaching-contracts";
import type { CoachingRole } from "../domain/roles";
import type {
  CoachingOperationResult,
  CoachingRecordInput,
  ParticipantBulkImportRequest,
  OtpRequest,
  OtpVerifyRequest,
  ProfileUpdateRequest
} from "../services/coaching-dtos";
import type { CoachingPlatformOrganizationCreateRequest } from "@vjal/api-contract";
import { getCoachingServices } from "../services/coaching-service-provider";
import { clearCoachingOrgId } from "../services/coaching-org-context";
import { useWalkReadinessHold } from "../lib/walk-readiness";
import { coachingQueryKeys, isAggregateSessionListContaining } from "./coaching-query-keys";
import {
  cacheCohortLifecycleReadback,
  cacheParticipantBulkImportReadback,
  cacheParticipantLifecycleReadback,
  cacheUserLifecycleReadback,
  invalidateCohortLifecycleReadbacks,
  invalidateUserLifecycleReadbacks
} from "./coaching-write-truth-caches";


const services = getCoachingServices();

export const authProfileQueryPolicy = {
  retry: false,
  staleTime: 0,
  refetchOnMount: "always" as const,
  // Window-focus refetching is deliberately OFF for the session query. With
  // staleTime:0 and no cached data, a focus refetch of an unauthenticated
  // (401) `/auth/me` flips the query back to `pending` -> isLoading true, which
  // makes the route screen swap the login surface for the "Checking your
  // session…" interstitial and unmounts the in-progress OTP flow (losing the
  // entered email + code step) when a signed-out user switches browser tabs.
  // Navigation still re-validates via refetchOnMount:"always".
  refetchOnWindowFocus: false
};

export function useCoachingProfile() {
  const query = useQuery({
    queryKey: coachingQueryKeys.profile(),
    queryFn: () => services.auth.me(),
    ...authProfileQueryPolicy
  });
  useWalkReadinessHold(query.isLoading);
  return query;
}
export function usePlatformOrganizationsQuery(enabled = true) {
  const query = useQuery({
    queryKey: [...coachingQueryKeys.root, "platform-organizations"],
    queryFn: () => services.platformOrganizations.list(),
    enabled,
    staleTime: 15_000
  });
  useWalkReadinessHold(enabled && query.isLoading);
  return query;
}

export function usePlatformOrganizationCreateMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CoachingPlatformOrganizationCreateRequest) =>
      services.platformOrganizations.create(body, createIdempotencyKey("platform-organization-create")),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [...coachingQueryKeys.root, "platform-organizations"] });
    }
  });
}

export function useAuthOtpMutations() {
  const queryClient = useQueryClient();

  return {
    requestOtp: useMutation({
      mutationFn: (body: OtpRequest) => services.auth.requestOtp(body, createIdempotencyKey("auth-otp-request"))
    }),
    verifyOtp: useMutation({
      mutationFn: (body: OtpVerifyRequest) => services.auth.verifyOtp(body, createIdempotencyKey("auth-otp-verify")),
      onSuccess: async (result) => {
        if (result.ok && result.profile) {
          queryClient.setQueryData(coachingQueryKeys.profile(), result.profile);
          await queryClient.invalidateQueries({ queryKey: coachingQueryKeys.profile() });
          return;
        }
        clearCoachingOrgId();
        queryClient.removeQueries({ queryKey: coachingQueryKeys.profile() });
      }
    }),
    logout: useMutation({
      mutationFn: () => services.auth.logout(createIdempotencyKey("auth-logout")),
      onSuccess: () => {
        clearCoachingOrgId();
        queryClient.removeQueries({ queryKey: coachingQueryKeys.root });
      }
    })
  };
}

export function useProfileUpdateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: ProfileUpdateRequest) => services.profile.update(body, createIdempotencyKey("profile-update")),
    onSuccess: async (result) => {
      if (!result.ok || !result.profile) return;
      queryClient.setQueryData(coachingQueryKeys.profile(), result.profile);
    }
  });
}

export function useCoachingDashboardFoundation({ enabled = true, role }: { enabled?: boolean; role?: CoachingRole } = {}) {
  const companiesEnabled = enabled && role !== "cohort_lead";
  const companies = useQuery({
    queryKey: coachingQueryKeys.list("companies.list"),
    queryFn: () => services.companies.list(),
    enabled: companiesEnabled
  });
  const cohorts = useQuery({
    queryKey: coachingQueryKeys.list("cohorts.list"),
    queryFn: () => services.cohorts.list(),
    enabled
  });
  const sessions = useQuery({
    queryKey: coachingQueryKeys.list("sessions.list"),
    queryFn: () => services.sessions.list(),
    enabled
  });
  const reports = useQuery({
    queryKey: coachingQueryKeys.list("reports.list"),
    queryFn: () => services.reports.list(),
    enabled
  });
  useWalkReadinessHold(companiesEnabled && companies.isLoading);
  useWalkReadinessHold(enabled && cohorts.isLoading);
  useWalkReadinessHold(enabled && sessions.isLoading);
  useWalkReadinessHold(enabled && reports.isLoading);
  return { companies, cohorts, sessions, reports };
}
export function useCoachingDashboardReadModel(role: CoachingRole) {
  const query = useQuery({
    queryKey: coachingQueryKeys.dashboard(role),
    queryFn: () => services.dashboard.composedRead(role),
    staleTime: 30_000
  });
  useWalkReadinessHold(query.isLoading);
  return query;
}


export function usePlatformReadinessMutation() {
  const queryClient = useQueryClient();
  const sideEffectSummary = getMutationSideEffectSummary("platformEvents.readiness");

  return {
    sideEffectSummary,
    mutation: useMutation({
      mutationFn: (body: unknown) => services.platformEvents.readiness(body, createIdempotencyKey("platform-events")),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: coachingQueryKeys.root })
    })
  };
}

export function useUserLifecycleMutations() {
  const queryClient = useQueryClient();

  return {
    sideEffectSummary: {
      create: getMutationSideEffectSummary("users.create"),
      update: getMutationSideEffectSummary("users.update"),
      deactivate: getMutationSideEffectSummary("users.deactivate")
    },
    create: useMutation({
      mutationFn: (body: CoachingRecordInput) => services.users.create(body, createIdempotencyKey("users-create")),
      onSuccess: (user) => cacheUserLifecycleReadback(queryClient, user)
    }),
    update: useMutation({
      mutationFn: ({ userId, body }: { userId: string; body: CoachingRecordInput }) =>
        services.users.update(userId, body, createIdempotencyKey(`users-update:${userId}`)),
      onSuccess: (user) => cacheUserLifecycleReadback(queryClient, user)
    }),
    deactivate: useMutation({
      mutationFn: (userId: string) => services.users.deactivate(userId, createIdempotencyKey(`users-deactivate:${userId}`)),
      onSuccess: async (result, userId) => {
        await queryClient.invalidateQueries({ queryKey: coachingQueryKeys.userDetail(result.id ?? userId) });
        await invalidateUserLifecycleReadbacks(queryClient);
      }
    })
  };
}

export function useCohortLifecycleMutations() {
  const queryClient = useQueryClient();

  return {
    sideEffectSummary: {
      create: getMutationSideEffectSummary("cohorts.create"),
      update: getMutationSideEffectSummary("cohorts.update"),
      complete: getMutationSideEffectSummary("cohorts.complete"),
      bulkImport: getMutationSideEffectSummary("participants.bulkImport")
    },
    create: useMutation({
      mutationFn: (body: CoachingRecordInput) => services.cohorts.create(body, createIdempotencyKey("cohorts-create")),
      onSuccess: (cohort) => cacheCohortLifecycleReadback(queryClient, cohort)
    }),
    update: useMutation({
      mutationFn: ({ cohortId, body }: { cohortId: string; body: CoachingRecordInput }) =>
        services.cohorts.update(cohortId, body, createIdempotencyKey(`cohorts-update:${cohortId}`)),
      onSuccess: (cohort) => cacheCohortLifecycleReadback(queryClient, cohort)
    }),
    complete: useMutation({
      mutationFn: (cohortId: string) => services.cohorts.complete(cohortId, createIdempotencyKey(`cohorts-complete:${cohortId}`)),
      onSuccess: async (result) => {
        if (result.ok && result.id) await queryClient.invalidateQueries({ queryKey: coachingQueryKeys.cohortDetail(result.id) });
        await invalidateCohortLifecycleReadbacks(queryClient, result.id, { includeLists: true });
      }
    }),
    bulkImport: useMutation({
      mutationFn: ({ cohortId, body }: { cohortId: string; body: ParticipantBulkImportRequest }) =>
        services.participants.bulkImport(cohortId, body, createIdempotencyKey(`participants-bulk-import:${cohortId}`)),
      onSuccess: async (result, variables) => {
        const cohortId = result.id ?? variables.cohortId;
        if (result.ok && cohortId) {
          await cacheParticipantBulkImportReadback(queryClient, result, cohortId);
        } else {
          await invalidateCohortLifecycleReadbacks(queryClient, cohortId, { includeLists: true });
        }
      }
    })
  };
}

export function useParticipantCreateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CoachingRecordInput) =>
      services.participants.create(body, createIdempotencyKey("participants-create")),
    onSuccess: (participant) => cacheParticipantLifecycleReadback(queryClient, participant)
  });
}

export function useSessionCreateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CoachingRecordInput) => services.sessions.create(body, createIdempotencyKey("sessions-create")),
    onSuccess: async (session) => {
      await invalidateParticipantSessionCreateReadbacks(queryClient, "sessions.list");
      if (session.cohort_id) {
        const cohortId = session.cohort_id;
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: coachingQueryKeys.list("sessions.listForCohort", { cohort_id: cohortId })
          }),
          queryClient.invalidateQueries({ predicate: (query) => isAggregateSessionListContaining(query.queryKey, cohortId) })
        ]);
      }
    }
  });
}

async function invalidateParticipantSessionCreateReadbacks(
  queryClient: QueryClient,
  family: "participants.list" | "sessions.list"
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list(family) }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("history.list") }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.recordScopeRoot() }),
    queryClient.invalidateQueries({ queryKey: [...coachingQueryKeys.root, "dashboard"] })
  ]);
}

export function createIdempotencyKey(scope: string) {
  // The scope label documents call-site intent only. Coaching API mutations
  // reject anything but a UUID Idempotency-Key (400 CO_VALIDATION_FAILED).
  void scope;
  return crypto.randomUUID();
}

export function getMutationSideEffectSummary(key: CoachingEndpointKey) {
  const contract = coachingEndpoints[key];
  return {
    requiresDbEvidence: contract.sideEffects.includes("db1-write") || contract.sideEffects.includes("readiness-evidence"),
    providerClosed: contract.sideEffects.includes("provider-closed"),
    idempotency: contract.idempotency,
    sideEffects: contract.sideEffects
  };
}

export function isProviderClosedResult(result: CoachingOperationResult | undefined, key: CoachingEndpointKey) {
  return Boolean(result?.side_effects?.includes("provider-closed") || getMutationSideEffectSummary(key).providerClosed);
}
